import React from 'react';
import { css } from 'emotion';
import { FeatureItem } from './components/FeatureItem';
import { ArrowLink } from './components/ArrowLink';
import { Press } from './components/Press';
import { AppButtons } from './components/AppButtons';
import { VideoPlayer } from './components/VideoPlayer';
import { Footer } from '../../components/Footer';
import Link from '../../components/Link';
import { WaveRule } from '../../components/WaveRule';
import { Button } from '../../shared/Button/NewButton';
import { getImgSrcSet } from '../../utils';
import { isIOS, isAndroidChrome } from '../../../helpers/serverRenderingUtils';
import { useBrowserSize } from '../../contexts/BrowserSize';

import {
  FEATURED_PODCASTS,
  FEATURES_CONTENT,
  LAPTOP_IMAGE_URLS,
} from './constants';

import { events } from './events';

import styles from './styles.sass';

function getIsMobileDevice() {
  return isIOS() || isAndroidChrome();
}

function getFeaturesHeaderText(width) {
  return getIsMobileDevice() ? (
    <h2 className={styles.featuresSectionHeader}>
      Create, distribute, and monetize your podcast —{' '}
      <span className={styles.featuresSectionHeaderHighlight}>
        right from your {width <= 600 ? 'phone' : 'phone or tablet'}.
      </span>
    </h2>
  ) : (
    <h2 className={styles.featuresSectionHeader}>
      Anchor is an all-in-one platform where you can{' '}
      <span className={styles.featuresSectionHeaderHighlight}>create</span>,
      {` `}
      <span className={styles.featuresSectionHeaderHighlight}>distribute</span>,
      and{' '}
      <span className={styles.featuresSectionHeaderHighlight}>monetize</span>{' '}
      your podcast from any device, for free.
    </h2>
  );
}

const HomePage = () => {
  const { width } = useBrowserSize();
  const isMobileBreakpoint = width <= 1024;
  const isMobileDevice = getIsMobileDevice();
  return (
    <div className={styles.homepage}>
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.heroSectionLeftContent}>
            <h1 className={styles.h1}>The easiest way to make a podcast.</h1>
            <p className={styles.heroSubheader}>
              Everything you need, 100% free.
            </p>
            <div className={styles.heroCtaWrapper}>
              <Link
                to="/signup"
                className={`cyanButton linkButton ${styles.heroCta}`}
                onClick={() => events.clickHeroSignUpCta()}
              >
                Make your podcast
              </Link>
              <AppButtons
                clickEventLocation="Homepage"
                clickEventSection="Hero"
                appStoreUrl="https://app.adjust.com/a3433pu?campaign=Homepage&adgroup=Apple%20App%20Store%20%28Top%20of%20fold%20%2D%20desktop%29&redirect=https%3A%2F%2Fapps.apple.com%2Fus%2Fapp%2Fanchor%2Fid1056182234"
                playStoreUrl="https://app.adjust.com/yzhnwg1?redirect=https%3A%2F%2Fanchor.fm%2Fapp"
                mobileAppStoreUrl="https://app.adjust.com/il5ifof?redirect=https%3A%2F%2Fanchor.fm%2Fapp"
                mobilePlayStoreUrl="https://app.adjust.com/yzhnwg1?redirect=https%3A%2F%2Fanchor.fm%2Fapp"
              />
            </div>
          </div>
          <div className={styles.heroSectionRightContent}>
            <img
              className={styles.heroImage}
              alt="laptop with a phone showing Anchor"
              src={LAPTOP_IMAGE_URLS[0]}
              srcSet={getImgSrcSet(LAPTOP_IMAGE_URLS)}
            />
          </div>
        </div>
      </section>
      <WaveRule />
      <section className={styles.featuresSection}>
        {getFeaturesHeaderText(width)}
        {FEATURES_CONTENT.map((featureContent, index) => {
          const {
            title,
            text,
            mobileText,
            svgType,
            linkUrl,
            imageWidth,
            svgWidths,
            mobileSvgWidths,
          } = featureContent;
          return (
            <FeatureItem
              pageWidth={width}
              key={title}
              title={title}
              text={text}
              svgType={svgType}
              linkUrl={linkUrl}
              imageWidth={imageWidth}
              svgWidths={svgWidths}
              mobileSvgWidths={mobileSvgWidths}
              alignImageRight={index % 2 === 1}
              mobileText={mobileText}
            />
          );
        })}
      </section>
      <WaveRule isInverted />
      <section className={styles.howToSection}>
        <div className={styles.howToContentContainer}>
          {isMobileBreakpoint && (
            <h2 className={styles.howToHeaderMobile}>
              How to make a podcast with Anchor
            </h2>
          )}
          <div className={styles.howToVideoWrapper}>
            <VideoPlayer />
          </div>
          <div className={styles.howToText}>
            {!isMobileBreakpoint && (
              <h2 className={styles.howToHeaderDesktop}>
                How to make a podcast with Anchor
              </h2>
            )}
            <ol className={styles.howToList}>
              <li>
                Sign up{' '}
                <Link
                  className={styles.howToLink}
                  to="/signup"
                  onClick={() => events.clickVideoSignUpLink()}
                >
                  here
                </Link>
                , or with the{' '}
                <Link
                  className={styles.howToLink}
                  href={
                    isMobileDevice
                      ? 'https://app.adjust.com/7jon25k?redirect=https%3A%2F%2Fanchor.fm%2Fapp'
                      : 'https://app.adjust.com/lf6ky6g?redirect=https%3A%2F%2Fapps.apple.com%2Fapp%2Fapple-store%2Fid1056182234'
                  }
                  onClick={() => events.clickVideoAppLink()}
                >
                  free app
                </Link>
              </li>
              <li>Just start recording! </li>
              <li>Hit publish and be heard everywhere.</li>
            </ol>
            <Link
              className={`cyanButton linkButton ${styles.howToCta}`}
              href="https://help.anchor.fm/hc/en-us/articles/360033428812"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => events.clickVideoLearnMoreLink()}
            >
              Learn more
            </Link>
          </div>
        </div>
      </section>
      <section className={styles.showSection}>
        <div className={styles.showSectionHeading}>
          <h2 className={styles.showSectionHeader}>
            Anchor powers more of the world’s podcasts than any other platform.
          </h2>
        </div>
        <div className={styles.showFeaturedWrapper}>
          <div className={styles.showFeaturedContainer}>
            {FEATURED_PODCASTS.map(podcast => {
              const { name, url, imageUrls } = podcast;
              return (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    className={styles.showFeaturedCover}
                    src={imageUrls[0]}
                    srcSet={getImgSrcSet(imageUrls)}
                    alt={`${name} podcast cover art`}
                  />
                </a>
              );
            })}
          </div>
        </div>
        <div className={styles.showSectionCta}>
          <h2 className={styles.showSectionCtaHeader}>
            Ready to make your own podcast?
          </h2>
          <Button
            kind="link"
            href="/signup"
            color="purple"
            className={css`
              width: 200px;
              margin-bottom: 24px;
            `}
            onClick={events.clickGetStartedCta}
          >
            Get started
          </Button>
          <p className={styles.showSectionText}>Already have a podcast?</p>
          <ArrowLink
            href="/switch"
            text="Switch to Anchor and never pay for hosting again"
            onClick={() => events.clickSwitchToAnchorLink()}
          />
        </div>
      </section>
      <section className={styles.pressSection}>
        <Press />
      </section>
      <Footer />
    </div>
  );
};

export { HomePage };
